import { create } from "zustand";

/**
 * StoryFlash — 화면 중앙에 큰 글씨로 잠깐 뜨는 "이야기 플래시".
 *
 * <p>토스트(우상단) 보다 강한 인식이 필요하지만 풀스크린 컷신(엔딩/레전드/영혼 MAX 등)
 * 까지는 아닌 중요도. 임계값 크로싱·동료 상태 전환·주요 경고 등에 사용.
 *
 * <p>tier 분기 (2026-04-22 E16 설계):
 * - `"mid"`: 화면 중앙 반투명 배너 (2.4s). dim 없음. 플레이 방해 최소. 임계값 크로싱 기본.
 * - `"high"`: 풀스크린 딤 + 중앙 대형 (3.4s). 사용자 결정 A.4 기준:
 *            엔딩 분기 확정 / 영혼 친밀도 MAX / 레전드 보상 / 동료 fallen / 장 전환.
 *
 * <p>동시에 여러 개가 쌓이면 큐 순차 재생. 한 번에 하나만 화면에 뜬다.
 */

export type StoryFlashTier = "mid" | "high";

export interface StoryFlashEntry {
    id: string;
    tier: StoryFlashTier;
    title: string;
    body?: string;
    /** 아이콘·이모지 글리프 (선택). high tier 에 특히 효과. */
    glyph?: string;
    /** 배경 액센트 색 override (토큰 색상 문자열). 기본: tier 별 기본색. */
    accent?: string;
    createdAt: number;
    /** 표시 시간. 미지정 시 tier 기본값. */
    durationMs: number;
}

interface StoryFlashState {
    queue: StoryFlashEntry[];
}

interface StoryFlashActions {
    push: (entry: Omit<StoryFlashEntry, "id" | "createdAt" | "durationMs"> & { durationMs?: number }) => string;
    shift: () => void;
    clear: () => void;
}

const DEFAULT_DURATION: Record<StoryFlashTier, number> = {
    mid: 2400,
    high: 3400,
};

export const useStoryFlash = create<StoryFlashState & StoryFlashActions>((set) => ({
    queue: [],

    push: (entry) => {
        const id = `flash-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
        const durationMs = entry.durationMs ?? DEFAULT_DURATION[entry.tier];
        const full: StoryFlashEntry = {
            ...entry,
            id,
            createdAt: Date.now(),
            durationMs,
        };
        set(state => ({ queue: [...state.queue, full] }));
        return id;
    },

    shift: () => set(state => ({ queue: state.queue.slice(1) })),
    clear: () => set({ queue: [] }),
}));

/** 외부에서 hook 없이 호출하기 위한 편의 함수. */
export function pushStoryFlash(entry: Omit<StoryFlashEntry, "id" | "createdAt" | "durationMs"> & { durationMs?: number }) {
    return useStoryFlash.getState().push(entry);
}
